import { useCallback, useEffect, useState } from "react"
import axios from "axios"
import {
  cancelPermit,
  getCancelReasons,
  getUserById,
  type CancelReasonOption,
  type PermitWorkflowTimeline,
} from "modules/permit/api/permitService"
import { isCancelledStatus, normalizePermitStatus } from "modules/permit/constants/permitStatus"
import { DRAFT_STATUS, GUID_PATTERN, coerceStringValue, normalizeGuidValue } from "../utils/pageHelpers"
import { requestNavCountsRefresh } from "modules/permit/utils/navCountRefresh"

interface UsePermitCancelFlowParams {
  tenantId: string
  permitId?: string | null
  permitNumber?: string
  permitStatus?: string | null
  permitMetadata?: Record<string, unknown> | null
  workflowTimeline?: PermitWorkflowTimeline | null
  cancelledByUserId?: string | null
  onCancelledSuccess: () => void
}

interface UsePermitCancelFlowResult {
  dialog: {
    open: boolean
    onOpenChange: (open: boolean) => void
    permitNumber: string
    cancelReasons: CancelReasonOption[]
    selectedReason: string
    onSelectedReasonChange: (value: string) => void
    reasonDetail: string
    onReasonDetailChange: (value: string) => void
    isLoadingReasons: boolean
    isCancelling: boolean
    error: string | null
    onConfirm: () => void
    onResetError: () => void
  }
  state: {
    cancelReasons: CancelReasonOption[]
    cancelActorName: string | null
    isLoadingReasons: boolean
    isCancelling: boolean
  }
  actions: {
    openDialog: () => void
  }
}

export function usePermitCancelFlow({
  tenantId,
  permitId,
  permitNumber,
  permitStatus,
  permitMetadata,
  workflowTimeline,
  cancelledByUserId,
  onCancelledSuccess,
}: UsePermitCancelFlowParams): UsePermitCancelFlowResult {
  const [open, setOpen] = useState(false)
  const [cancelReasons, setCancelReasons] = useState<CancelReasonOption[]>([])
  const [selectedReason, setSelectedReason] = useState("")
  const [reasonDetail, setReasonDetail] = useState("")
  const [isLoadingReasons, setIsLoadingReasons] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cancelActorName, setCancelActorName] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)

  useEffect(() => {
    const currentStatus = normalizePermitStatus(permitStatus) ?? DRAFT_STATUS
    const shouldLoadReasons =
      (open || isCancelledStatus(currentStatus)) && cancelReasons.length === 0 && !isLoadingReasons

    if (!shouldLoadReasons) {
      return
    }

    setIsLoadingReasons(true)
    setError(null)
    getCancelReasons(tenantId)
      .then((items) => setCancelReasons(items))
      .catch((fetchError) => {
        console.error("Failed to load cancel reasons", fetchError)
        setError("ไม่สามารถโหลดเหตุผลการยกเลิกได้")
      })
      .finally(() => setIsLoadingReasons(false))
  }, [cancelReasons.length, isLoadingReasons, open, permitStatus, tenantId])

  useEffect(() => {
    const metadata = permitMetadata ?? {}
    const metaNameRaw =
      coerceStringValue((metadata as any).cancelByUserName) ||
      coerceStringValue((metadata as any).cancelByName) ||
      coerceStringValue((metadata as any).cancelByDisplayName) ||
      coerceStringValue((metadata as any).cancelBy)
    const metaName = metaNameRaw && GUID_PATTERN.test(metaNameRaw.trim()) ? "" : metaNameRaw

    if (metaName) {
      setCancelActorName(metaName)
      return
    }

    const cancelById =
      normalizeGuidValue((metadata as any).cancelByUserId) ??
      normalizeGuidValue((metadata as any).cancelBy) ??
      normalizeGuidValue((metadata as any).cancelledBy) ??
      normalizeGuidValue(
        (workflowTimeline?.history ?? []).find((history) => history.statusAfter?.toUpperCase() === "CANCELLED")
          ?.actedByUserId,
      ) ??
      normalizeGuidValue(
        (workflowTimeline?.history ?? [])
          .find((history) => {
            const actedByName = history.actedByUserName ?? ""
            return GUID_PATTERN.test(actedByName.trim())
          })
          ?.actedByUserName,
      )

    if (!cancelById) {
      setCancelActorName(null)
      return
    }

    let cancelled = false
    getUserById(cancelById, tenantId)
      .then((user) => {
        if (cancelled) return
        const resolvedName = coerceStringValue(user?.fullName) || coerceStringValue(user?.userName)
        setCancelActorName(resolvedName || null)
      })
      .catch(() => {
        if (cancelled) return
        setCancelActorName(null)
      })

    return () => {
      cancelled = true
    }
  }, [permitMetadata, tenantId, workflowTimeline])

  const handleConfirm = useCallback(async () => {
    if (!permitId) {
      setError("ยังไม่พบเลขที่ Permit")
      return
    }
    if (!selectedReason) {
      setError("กรุณาเลือกเหตุผลการยกเลิก")
      return
    }

    setIsCancelling(true)
    setError(null)

    try {
      await cancelPermit(permitId, {
        tenantId,
        permitId,
        cancelledBy: cancelledByUserId ?? undefined,
        reasonCode: selectedReason,
        reasonDetail: reasonDetail.trim() || undefined,
      })

      setOpen(false)
      setSelectedReason("")
      setReasonDetail("")
      requestNavCountsRefresh()
      onCancelledSuccess()
    } catch (submitError) {
      console.error("Failed to cancel permit", submitError)
      let message = "ยกเลิกเอกสารไม่สำเร็จ กรุณาลองใหม่"
      if (axios.isAxiosError(submitError)) {
        message =
          (submitError.response?.data as any)?.message ??
          submitError.response?.statusText ??
          submitError.message ??
          message
      }
      setError(message)
    } finally {
      setIsCancelling(false)
    }
  }, [cancelledByUserId, onCancelledSuccess, permitId, reasonDetail, selectedReason, tenantId])

  return {
    dialog: {
      open,
      onOpenChange: setOpen,
      permitNumber: permitNumber ?? "",
      cancelReasons,
      selectedReason,
      onSelectedReasonChange: setSelectedReason,
      reasonDetail,
      onReasonDetailChange: setReasonDetail,
      isLoadingReasons,
      isCancelling,
      error,
      onConfirm: handleConfirm,
      onResetError: () => setError(null),
    },
    state: {
      cancelReasons,
      cancelActorName,
      isLoadingReasons,
      isCancelling,
    },
    actions: {
      openDialog: () => setOpen(true),
    },
  }
}
